// Fake component controller so this code is autoloaded
//
// Keeps the footer's divider offset correct when the server-rendered value can no longer be
// trusted — which in the editor is most of the time.
//
// Builder::TrailingDivider publishes `--previous-node-divider-height` onto this node's next sibling
// (the footer) at render time, from what the page's sections reported. In the editor that CSS goes
// stale: nodes are replaced one at a time, and this element carries `data-preserve-during-replace`,
// so its <style> is deliberately carried over rather than re-rendered. Adding or hiding a section
// therefore changes which section is last without changing the published value.
//
// So we re-derive it from the DOM, which is always current: the last *visible* top-level section
// inside this node, and its own `--current-node-divider-height`.
//
// Reading computed style makes this automatically correct per breakpoint — and is exactly why it
// must re-run on resize. The inline value we write outranks all three server rules, so a value
// picked up at desktop width would otherwise persist after switching the previewer to mobile.
// When the DOM already agrees with the stylesheet we clear the inline value instead of setting it,
// so the server rules stay in charge whenever they are right.
import { Controller } from 'stimulus'
import {
  lastVisibleDividerHeight,
  shouldOverride,
  NO_DIVIDER,
} from './trailing_divider_helpers.js'

const VARIABLE = '--previous-node-divider-height'
const OWN_VARIABLE = '--current-node-divider-height'

// Top-level sections of the page body nested inside this placeholder. Scoped through
// `.builder-body` so a nested doc's sections can't be mistaken for the page's own, and the
// layout's body — which is this node's *ancestor*, not its descendant — is out of reach.
const SECTION_SELECTOR =
  ':scope .builder-body > .js-node-content > .builder-section'

export default class extends Controller {
  connect() {
    this.reconcile()
  }

  disconnect() {
    if (this.frame) cancelAnimationFrame(this.frame)
    this.frame = null
    this.#clear()
  }

  // `builder:repositionControls` fires three times per canvas change by design (immediately, then
  // on the next two frames, so tiptap and fonts can settle). Each pass reads computed style per
  // section, which forces a style recalc — so coalesce to one pass per frame.
  reconcile() {
    if (this.frame) return

    this.frame = requestAnimationFrame(() => {
      this.frame = null
      this.#reconcileNow()
    })
  }

  #reconcileNow() {
    const target = this.element.nextElementSibling
    if (!target) return

    // Clear first so the read below sees what the stylesheet alone says, not our own last write.
    this.#clear()

    const fromDom = lastVisibleDividerHeight(this.#sectionDescriptors())
    const fromStylesheet = this.#readVariable(target, VARIABLE)

    if (!shouldOverride(fromDom, fromStylesheet)) return

    target.style.setProperty(VARIABLE, fromDom)
    this.appliedTo = target
  }

  // DOM -> plain descriptors, so the decision itself stays pure and testable.
  #sectionDescriptors() {
    return [...this.element.querySelectorAll(SECTION_SELECTOR)].map(
      (section) => ({
        hidden: getComputedStyle(section).display === 'none',
        height: this.#readVariable(section, OWN_VARIABLE),
      })
    )
  }

  #readVariable(element, name) {
    return getComputedStyle(element).getPropertyValue(name).trim() || NO_DIVIDER
  }

  #clear() {
    this.appliedTo?.style?.removeProperty(VARIABLE)
    this.appliedTo = null
  }
}
